import { useState } from 'react';
import pLimit from 'p-limit';
import { useToast } from '../../components/Toast';
import type { ApiEndpoints } from '@avisoma/shared';

type CategoryRow = ApiEndpoints<Date>['/api/categories']['GET']['response']['items'][number];

interface BatchCategoryActionOptions {
  items: CategoryRow[];
  /** Human-readable verb, e.g. "Training" or "Reclassifying" */
  verb: string;
  /** Calls the backend for one category. Should reject on error so the toast fires. */
  action: (id: string) => Promise<unknown>;
  errorMessage: string;
  onSuccess: () => Promise<unknown>;
  onAllError: (err: unknown) => void;
}

/**
 * Shared helper for train-all and reclassify-all flows.
 * Returns `{ loading, run }` where `loading` is false when idle or a progress
 * string like "Training 45%..." while the batch is running.
 */
export function useBatchCategoryAction({
  items,
  verb,
  action,
  errorMessage,
  onSuccess,
  onAllError,
}: BatchCategoryActionOptions) {
  const [loading, setLoading] = useState<false | string>(false);
  const toast = useToast();

  const run = () => {
    setLoading(`${verb}...`);
    const count = items.length;
    let doneCount = 0;
    const limit = pLimit(10);

    Promise.all(
      items.map((item) =>
        limit(async () => {
          await action(item.id.toString()).catch((err) => {
            toast.error(err instanceof Error ? err.message : errorMessage);
          });
          doneCount++;
          setLoading(`${verb} ${((doneCount / count) * 100).toFixed(0)}%...`);
        }),
      ),
    )
      .then(() => onSuccess())
      .catch((err) => onAllError(err))
      .finally(() => setLoading(false));
  };

  return { loading, run };
}
